/* LYNE Canvas Image Sequence Loader
   Reusable scrubbing-image-sequence renderer.
   No external dependencies. GSAP-frei, intentionally.
   prefers-reduced-motion: Sequence wird auf statisches Hero-Frame reduziert.
   Mobile-Frame-Halbierung: Daten-Attribut data-lyne-seq-mobile-cap setzt Cap.

   Verwendung:
     <div class="lyne-seq"
          data-lyne-seq-base="https://cdn.shopify.com/.../onyx-frame-"
          data-lyne-seq-suffix=".webp"
          data-lyne-seq-pad="3"
          data-lyne-seq-count="120"
          data-lyne-seq-mobile-cap="60"
          data-lyne-seq-fit="cover">
       <canvas></canvas>
     </div>

   Stand: 2026-05-13. Builder Loop 1. */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  var PREFERS_REDUCED = false;
  try {
    PREFERS_REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { /* IE/Edge alt: kein matchMedia */ }

  var IS_MOBILE = window.innerWidth < 750;

  function LyneSeq(root) {
    this.root = root;
    this.canvas = root.querySelector('canvas');
    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
      root.appendChild(this.canvas);
    }
    this.ctx = this.canvas.getContext('2d', { alpha: false });

    this.base   = root.getAttribute('data-lyne-seq-base')   || '';
    this.suffix = root.getAttribute('data-lyne-seq-suffix') || '.webp';
    this.pad    = parseInt(root.getAttribute('data-lyne-seq-pad') || '3', 10);
    this.count  = parseInt(root.getAttribute('data-lyne-seq-count') || '0', 10);
    this.mobileCap = parseInt(root.getAttribute('data-lyne-seq-mobile-cap') || '0', 10);
    this.fit    = root.getAttribute('data-lyne-seq-fit') || 'cover'; // cover | contain
    this.startFrame = parseInt(root.getAttribute('data-lyne-seq-start') || '1', 10);

    if (IS_MOBILE && this.mobileCap > 0 && this.mobileCap < this.count) {
      this.count = this.mobileCap;
    }

    this.frames = [];
    this.loaded = 0;
    this.current = 0;
    this.ready = false;
    this._raf = null;
    this._resizeHandler = this._resize.bind(this);
    this._scrollHandler = this._onScroll.bind(this);

    if (!this.base || !this.count) {
      // Kein Asset konfiguriert. Stille Degradation.
      return;
    }

    this._preload();
    this._resize();
    window.addEventListener('resize', this._resizeHandler, { passive: true });
    window.addEventListener('scroll', this._scrollHandler, { passive: true });
  }

  LyneSeq.prototype._frameSrc = function (i) {
    var n = String(i);
    while (n.length < this.pad) n = '0' + n;
    return this.base + n + this.suffix;
  };

  LyneSeq.prototype._preload = function () {
    var self = this;
    var firstIdx = Math.max(this.startFrame, 1);
    var count = this.count;

    // Erstes Frame priorisiert laden (LCP-Asset)
    var firstImg = new Image();
    firstImg.onload = function () {
      self.frames[0] = firstImg;
      self.ready = true;
      self._draw(0);
    };
    firstImg.onerror = function () {
      self._showFallback();
    };
    firstImg.src = this._frameSrc(firstIdx);

    if (PREFERS_REDUCED) {
      // In reduced-motion bleibt es bei Frame 1 als statischem Hero.
      return;
    }

    // Restliche Frames lazy nachladen
    var loadNext = function (idx) {
      if (idx >= count) return;
      var img = new Image();
      img.onload = function () {
        self.frames[idx] = img;
        self.loaded++;
        loadNext(idx + 1);
      };
      img.onerror = function () {
        // Frame nicht da: ueberspringen, weiter mit naechstem
        loadNext(idx + 1);
      };
      img.src = self._frameSrc(firstIdx + idx);
    };
    // Start mit Frame Index 1 (Frame 0 wurde bereits geladen)
    loadNext(1);
  };

  LyneSeq.prototype._showFallback = function () {
    // Fallback: SVG-Placeholder, dunkler Hintergrund
    if (!this.ctx) return;
    var w = this.canvas.width;
    var h = this.canvas.height;
    this.ctx.fillStyle = '#0A0A0A';
    this.ctx.fillRect(0, 0, w, h);
  };

  LyneSeq.prototype._resize = function () {
    if (!this.canvas) return;
    var rect = this.root.getBoundingClientRect();
    var dpr = window.devicePixelRatio || 1;
    this.canvas.width  = Math.floor(rect.width  * dpr);
    this.canvas.height = Math.floor(rect.height * dpr);
    this.canvas.style.width  = rect.width  + 'px';
    this.canvas.style.height = rect.height + 'px';
    this._draw(this.current);
  };

  LyneSeq.prototype._onScroll = function () {
    if (PREFERS_REDUCED) return;
    if (this._raf) return;
    var self = this;
    this._raf = window.requestAnimationFrame(function () {
      self._raf = null;
      self._update();
    });
  };

  LyneSeq.prototype._update = function () {
    var rect = this.root.getBoundingClientRect();
    var viewportH = window.innerHeight || document.documentElement.clientHeight;
    // Progress 0 wenn Section gerade ins Viewport eintritt, 1 wenn sie verlaesst
    var sectionH = rect.height || 1;
    var totalScrollable = sectionH + viewportH;
    var scrolled = viewportH - rect.top;
    var p = Math.max(0, Math.min(1, scrolled / totalScrollable));
    var idx = Math.min(this.count - 1, Math.floor(p * this.count));
    if (idx !== this.current) {
      this.current = idx;
      this._draw(idx);
    }
  };

  LyneSeq.prototype._draw = function (idx) {
    if (!this.ctx) return;
    var img = this.frames[idx] || this.frames[0];
    if (!img) return;
    var cw = this.canvas.width;
    var ch = this.canvas.height;
    this.ctx.fillStyle = '#000';
    this.ctx.fillRect(0, 0, cw, ch);

    var iw = img.naturalWidth;
    var ih = img.naturalHeight;
    if (!iw || !ih) return;

    var sx, sy, sw, sh, dx, dy, dw, dh;
    if (this.fit === 'contain') {
      var rContain = Math.min(cw / iw, ch / ih);
      dw = iw * rContain;
      dh = ih * rContain;
      dx = (cw - dw) / 2;
      dy = (ch - dh) / 2;
      this.ctx.drawImage(img, dx, dy, dw, dh);
    } else {
      // cover
      var rCover = Math.max(cw / iw, ch / ih);
      dw = iw * rCover;
      dh = ih * rCover;
      dx = (cw - dw) / 2;
      dy = (ch - dh) / 2;
      this.ctx.drawImage(img, dx, dy, dw, dh);
    }
  };

  function init() {
    var nodes = document.querySelectorAll('.lyne-seq');
    for (var i = 0; i < nodes.length; i++) {
      // Doppelinit verhindern
      if (nodes[i].dataset.lyneSeqInit === '1') continue;
      nodes[i].dataset.lyneSeqInit = '1';
      new LyneSeq(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init nach Shopify-section-Edit (Customizer)
  if (typeof document !== 'undefined') {
    document.addEventListener('shopify:section:load', init);
  }

  // Export fuer manuelle Steuerung
  window.LyneSeq = LyneSeq;
})();
